import { useCallback, useState, useEffect, useRef, CSSProperties, FC, ChangeEvent } from 'react';
import {
  Box,
  TextField,
  CircularProgress,
  Typography,
  MenuItem,
  InputAdornment,
  ClickAwayListener,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import useDebounce from '@hooks/useDebounce';
import { SEARCH_DEBOUNCE_TIME, VIRTUALIZER_ESTIMATE_SIZE } from '@constants/constants';
import useTanstackVirtualizer from '@hooks/useTanstackVirtualizer';
import { SelectOption } from '@api/types/common';

const DROPDOWN_MAX_HEIGHT = 250;

interface InfiniteSearchSelectProps {
  options: SelectOption[];
  hasNextPage: boolean;
  isFetching: boolean;
  fetchNextPage: () => void;
  onSelect: (option: SelectOption) => void;
  isError?: boolean;
  isLoading: boolean;
  placeholder?: string;
  onSearchChange: (searchTerm: string) => void;
  selectedValue: string;
  selectStyles?: CSSProperties;
  menuStyles?: CSSProperties;
  menuItemStyles?: CSSProperties;
  noOptionsText?: string;
  errorText?: string;
  searchEnabled?: boolean;
}

const InfiniteSearchSelect: FC<InfiniteSearchSelectProps> = ({
  options,
  hasNextPage,
  isFetching,
  fetchNextPage,
  onSelect,
  isError,
  isLoading,
  placeholder = 'Search',
  onSearchChange,
  selectedValue,
  selectStyles,
  menuStyles,
  menuItemStyles,
  noOptionsText = 'No items available',
  errorText = 'Error loading items',
  searchEnabled = true,
}) => {
  const [searchTerm, setSearchTerm] = useState(selectedValue);
  const [showList, setShowList] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>('bottom');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { rowVirtualizer } = useTanstackVirtualizer({
    rows: options,
    parentRef: dropdownRef,
    hasNextPage,
    isFetchingNextPage: isFetching,
    fetchNextPage,
  });

  useEffect(() => {
    if (showList && selectedValue && dropdownRef.current) {
      const selectedIndex = options.findIndex((opt) => opt.value === selectedValue);
      if (selectedIndex !== -1) {
        rowVirtualizer.scrollToIndex(selectedIndex, { align: 'auto' });
      }
    }
  }, [showList, selectedValue, options, rowVirtualizer]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!searchEnabled) return;

    const value = event.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const debouncedSearch = useDebounce((value: string) => {
    if (searchEnabled) {
      onSearchChange(value);
    }
  }, SEARCH_DEBOUNCE_TIME);

  const calculateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = menuStyles?.maxHeight
      ? Number(menuStyles.maxHeight)
      : DROPDOWN_MAX_HEIGHT;

    const spaceBelow = viewportHeight - containerRect.bottom;
    const spaceAbove = containerRect.top;

    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      setDropdownPosition('top');
    } else {
      setDropdownPosition('bottom');
    }
  }, []);

  const handleItemClick = (item: SelectOption) => {
    onSelect(item);
    setSearchTerm(item.label);
  };

  const handleClickAway = useCallback(() => {
    if (showList) {
      setShowList(false);
      setSearchTerm(selectedValue);
      if (searchEnabled) {
        onSearchChange('');
      }
    }
  }, [showList, selectedValue, onSearchChange, searchEnabled]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        ref={containerRef}
        sx={{ width: '100%', position: 'relative', ...selectStyles }}
        onFocus={() => {
          calculateDropdownPosition();
          setShowList(true);
        }}
      >
        <TextField
          fullWidth
          placeholder={placeholder}
          value={searchTerm ?? selectedValue}
          onChange={handleSearchChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <ArrowDropDownIcon />
              </InputAdornment>
            ),
            readOnly: !searchEnabled,
          }}
        />
        {showList && (
          <Box
            ref={dropdownRef}
            tabIndex={-1}
            sx={{
              height: 'fit-content',
              maxHeight: DROPDOWN_MAX_HEIGHT,
              overflow: 'auto',
              border: '1px solid #ddd',
              borderRadius: 1,
              position: 'absolute',
              backgroundColor: 'white',
              top: dropdownPosition === 'bottom' ? 57 : 'auto',
              bottom: dropdownPosition === 'top' ? 57 : 'auto',
              left: 0,
              width: '100%',
              zIndex: 1000,
              ...menuStyles,
            }}
          >
            {isError && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="error">{errorText}</Typography>
              </Box>
            )}

            {!isLoading && options.length === 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="textSecondary">{noOptionsText}</Typography>
              </Box>
            )}

            {options.length > 0 && (
              <div
                key={`virtual-container-${options.length}`}
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const option = options[virtualRow.index];
                  const isNextPageLoading = virtualRow.index >= options.length;

                  if (isNextPageLoading) {
                    return (
                      <Box
                        key={virtualRow.key}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          transform: `translateY(${virtualRow.start}px)`,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: VIRTUALIZER_ESTIMATE_SIZE,
                          width: '100%',
                        }}
                      >
                        <CircularProgress size={20} />
                      </Box>
                    );
                  }

                  if (!option) return null;
                  return (
                    <div
                      data-index={virtualRow.index}
                      key={`virtual-item-${virtualRow.index}-${option.value}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      <MenuItem
                        role="menuitem"
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: selectedValue === option.value ? '#2f736e14' : 'white',
                          height: VIRTUALIZER_ESTIMATE_SIZE,
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                            ...menuItemStyles,
                          },
                        }}
                        onClick={() => {
                          setShowList(false);
                          handleItemClick(option);
                        }}
                      >
                        {option.label}
                      </MenuItem>
                    </div>
                  );
                })}
              </div>
            )}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default InfiniteSearchSelect;
